import { Component, type ErrorInfo, type ReactNode } from 'react';
import { hideSplash } from '../capacitorInit';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Catches any otherwise-uncaught render error so the app shows a visible,
// recoverable screen instead of a silently stuck blank/splash screen
// (the failure mode that made two real bugs very hard to diagnose on iOS).
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Uncaught render error:', error, info.componentStack);
    hideSplash();
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-[#F8F9FF] flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <h1 className="text-xl font-bold text-slate-800 mb-2">Something went wrong</h1>
            <p className="text-sm text-slate-500 mb-4">{this.state.error.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
